import { useCallback } from 'react';

import { useCookies } from 'react-cookie';
import { v4 as uuid, v4 } from 'uuid';

import { WebsiteVisitPayload } from 'src/api/zrm';
import useApi from 'src/hooks/useApi';
import useSettings from 'src/hooks/useSettings';
import logger from 'src/utils/logger';

const useWebsiteVisitCallback = () => {
  const { api } = useApi();
  const requestId = uuid();
  const { country } = useSettings();

  const websiteVisitCall = useCallback(async () => {
    const data: WebsiteVisitPayload = {
      landing_url: window.location.href,
      cookie_consent: null,
    };

    try {
      await api.v2.createWebsiteVisitEventV2VisitCountryPost(
        country,
        data,
        { headers: { 'X-Request-ID': requestId }, cancelToken: requestId },
      );
    } catch (error) {
      logger.error(error, {
        source: 'useWebsiteVisitCallback',
        description: 'Send Website Visit',
        requestId,
      });
    }
    logger.log('Website visit event sent');
  }, []);

  return websiteVisitCall;
};

export default useWebsiteVisitCallback;

const setWebsiteVisitCookies = () => {
  const [cookies, setCookie] = useCookies(['zid', 'szid']);
  let { zid, szid } = cookies;

  // if user visits subdomain, before main domain
  // in a browser thats never been used to visit main domain or cookies has been cleared
  // we need to set a new zid and szid
  const setCookies = useCallback(() => {
    if (!szid) {
      const newSzid = v4().replace(/-/g, '');
      const domain = window.location.hostname.split('.').slice(-2).join('.');
      setCookie('szid', newSzid, {
        domain,
        path: '/',
        secure: true,
        sameSite: 'none',
      });
      szid = newSzid;
    }

    if (!zid) {
      const newZid = v4().replace(/-/g, '');
      const domain = window.location.hostname.split('.').slice(-2).join('.');
      setCookie('zid', newZid, {
        domain,
        path: '/',
        secure: true,
        sameSite: 'none',
      });
      zid = newZid;
    }

    localStorage.setItem('szid', szid);
    localStorage.setItem('zid', zid);
  }, [cookies, setCookie]);

  return setCookies;
};

const setCookiesThenWebsiteVisit = () => {
  const setCookies = setWebsiteVisitCookies();
  const websiteVisitCallback = useWebsiteVisitCallback();
  const localSessionZid = localStorage.getItem('szid');

  const setCookiesAndSendVisit = useCallback(() => {
    setCookies();

    // This is to avoid sending twice in same session
    if (localSessionZid === localStorage.getItem('szid')) return;

    websiteVisitCallback();
  }, [setCookies, websiteVisitCallback]);

  return setCookiesAndSendVisit;
};

export { setWebsiteVisitCookies, setCookiesThenWebsiteVisit };
